import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from 'src/lib/utils';
import type { PortalUser } from '../types/users.types';

interface UserActionsMenuProps {
  user: PortalUser;
  disabled?: boolean;
  onBlock: () => void;
  onUnblock: () => void;
}

export function UserActionsMenu({ user, disabled = false, onBlock, onUnblock }: UserActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const items = user.status === 'blocked'
    ? [{ label: 'Unblock user', onSelect: onUnblock }]
    : [{ label: 'Block user', onSelect: onBlock }];

  return (
    <div
      ref={containerRef}
      className="relative flex justify-end"
    >
      <button
        type="button"
        disabled={disabled}
        className={cn(
          'inline-flex size-8 items-center justify-center rounded-lg border border-transparent text-[#5E6278] transition-colors hover:border-[#D9DBE3] hover:bg-[#F0F1F5] hover:text-[#0D0F1A] disabled:cursor-not-allowed disabled:opacity-50',
          open && 'border-[#D9DBE3] bg-[#F0F1F5] text-[#0D0F1A]'
        )}
        aria-label={`Actions for ${user.name}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
      >
        <MoreHorizontal className="size-4" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 min-w-[180px] overflow-hidden rounded-lg border border-[#EDEEF2] bg-white p-1 shadow-[0_4px_16px_rgba(13,15,26,0.10)]"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="flex w-full rounded-md px-3 py-2 text-left text-[13px] text-[#0D0F1A] transition-colors hover:bg-[#F0F1F5]"
              onClick={() => {
                item.onSelect();
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
